import { createActions } from 'reduxsauce';

export type CommunityPagesByType = {
  donate: IPage[];
  event: IPage[];
  recommendation: IPage[];
};

export interface IPageState {
  data: IPageData;
  isLoading: boolean;
  error: string;
  communityPagesLoading: boolean;
}
export interface IPageData {
  clientPages: IClientPages;
  allPages: {
    pages: IPage[];
  };
  pageDetail: {
    pages: IPage;
  };
  pageCreated: IPageCreated;
  communityPagesByType: CommunityPagesByType | null;
}
export interface IClientPages {
  pages: IPage[];
  render_hints: IRenderHints;
  preferred_payment_processor: string;
}
export interface IPageCreated {
  query_string?: string;
  page_name?: string;
  page_id?: number;
  event_id?: string;
  id?: number;
}

export type IPausedMisc = {
  paused_description: string;
  paused_redirect_button: string;
  paused_redirect_url: string;
};

export type OathRecurringInterval = 'week' | 'month' | 'quarter' | 'year';

export type AllocationType = 'EVEN' | 'WEIGHTED' | 'WATERFALL';

export type RecipientGroup = {
  id?: string;
  page_id?: number;
  weight_percent: number | null;
  recipient_ids: string[];
  priority?: number;
  donation_limit_cents?: number | null;
  group_label?: string | null;
};

export type CommonPage = {
  value: string;
  query_str: string;
  client_id: number;
  is_enabled: boolean;
  desc: string;
  page_background: string | null;
  page_color_text: string | null;
  page_type: 'LIST' | 'COMPACT' | 'REC';
  recipient_ids: string[];
  paused_misc: IPausedMisc;
  fundraising_goal: number;
  display_fundraising_bar: boolean;
  learn_more_link: string;
  bottom_url_text?: string;
  page_category: string;
  whitelabeled: boolean;
  active_page_status: boolean;
  custom_disclaimer: string;
  has_image: boolean;
  has_community_image: boolean;
  has_optional_media?: boolean;
  optional_file_arrangement?: string;
  optional_media_type?: string | null;
  image_filenames?: {
    principal_logo?: string;
    community_logo?: string;
    optional_media?: string;
  };
  status: string;
  processing_redirect: string;
  is_public_searchable: boolean;
  is_oath_disclaimer_visible: boolean;
  recurring_enabled?: boolean;
  recurring_interval?: OathRecurringInterval | null;
  wire_enabled?: boolean;
  offline_instructions_wire?: { label: string; value: string }[];
  offline_instructions_check?: { label: string; value: string }[];
  offline_instructions_grant?: { label: string; value: string }[];
  allocation_type?: AllocationType;
  recipient_groups?: RecipientGroup[];
};

export type IPage = CommonPage & {
  id: number;
  start_datetime: string;
  updated_date: string;
  donation_amounts: number[];
  status: string;
  processing_redirect: string;
  de_page: string;
  event_id: string;
};

export type BodyCreateProps<T extends 'donate' | 'event' | 'recommendation'> = T extends 'donate'
  ? DonatePageProps
  : T extends 'event'
    ? EventPageProps
    : RecommendationPageProps;

export type DonatePageProps = CommonPage & {
  page_cta: string;
  impact_score_visibility: boolean;
  show_more_visibility: boolean;
  donation_amounts: number[];
};
export type EventPageProps = CommonPage & {
  page_cta: string;
  impact_score_visibility: boolean;
  show_more_visibility: boolean;
  subdescription: string;
  location_name: string;
  start_datetime: string;
  end_datetime: string | null;
};
export type RecommendationPageProps = CommonPage & {
  subdescription: string;
  donation_amounts: null;
  recipient_details: string[];
  recipient_order_by: string;
  subtitle: string;
};

export type BodyUpdateProps<T extends 'donate' | 'event' | 'recommendation'> = T extends 'donate'
  ? DonateBodyUpdateProps
  : T extends 'event'
    ? EventBodyUpdateProps
    : RecommendationBodyUpdateProps;

export type DonateBodyUpdateProps = {
  client_id: number;
  page: CommonPage & DonatePageProps;
};

export type EventBodyUpdateProps = CommonPage &
  EventPageProps & {
    client_id: number;
  };

export type RecommendationBodyUpdateProps = {
  client_id: number;
  page: RecommendationPageProps;
};

export type IFile = {
  name: string;
  size: number;
  type: string;
};

export type ITicket = {
  name: string;
  description?: string;
  price: number;
  quantity: number;
  quantity_remaining?: number;
  status: string;
  order: number;
  id: string;
};

export interface GetSignedUrlConfig {
  action: 'read' | 'write' | 'delete' | 'resumable';
  expires: number | string;
  version?: 'v2' | 'v4';
  cname?: string;
  contentMd5?: string;
  contentType?: string;
  promptSaveAs?: string;
  responseDisposition?: string;
  responseType?: string;
  virtualHostedStyle?: boolean;
}

export interface IRenderHints {
  field_restrictions: IFieldRestrictions;
}

export interface IFieldRestrictions {
  [fieldName: string]: IFieldPermissions;
}

export interface IFieldPermissions {
  can_see: boolean;
  can_update: boolean;
  can_create: boolean;
}

export type FundraisingPage = {
  latest_fundraising_transactions: FundraisingTransaction[];
  learn_more_link: string;
  page_fundraising_donation_count: number;
  page_fundraising_goal: string;
  page_fundraising_raised: string;
  page_id: number;
};

export type FundraisingTransaction = {
  amount: string;
  city: string;
  state: string;
  transaction_date: string;
};

export interface PagesTypes {
  PAGES: 'PAGES';
  PAGES_START: 'PAGES_START';
  ALL_PAGES_SUCCESS: 'ALL_PAGES_SUCCESS';
  PAGES_SUCCESS: 'PAGES_SUCCESS';
  PAGES_ERROR: 'PAGES_ERROR';
  PAGE_CREATED_SUCCESS: 'PAGE_CREATED_SUCCESS';
  PAGE_CREATED_ERROR: 'PAGE_CREATED_ERROR';
  PAGE_DETAIL_SUCCESS: 'PAGE_DETAIL_SUCCESS';
  PAGE_DETAIL_ERROR: 'PAGE_DETAIL_ERROR';
  PAGE_CLEAN_UP: 'PAGE_CLEAN_UP';
  COMMUNITY_PAGES_START: 'COMMUNITY_PAGES_START';
  COMMUNITY_PAGES_SUCCESS: 'COMMUNITY_PAGES_SUCCESS';
  COMMUNITY_PAGES_ERROR: 'COMMUNITY_PAGES_ERROR';
}

const { Types, Creators } = createActions<PagesTypes>({
  pages: ['data'],
  pagesStart: null,
  allPagesSuccess: ['data'],
  pagesSuccess: ['data'],
  pagesError: ['error'],
  pageCreatedSuccess: ['data'],
  pageCreatedError: ['error'],
  pageDetailSuccess: ['data'],
  pageDetailError: ['error'],
  pageCleanUp: null,
  communityPagesStart: null,
  communityPagesSuccess: ['data'],
  communityPagesError: ['error'],
});

export { Types };

export default Creators;
